import { ActionIcon, Tooltip } from '@mantine/core';
import { IconArrowRight, IconPlayerPlayFilled } from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import { stepIdToScrollToAtom, stepSearchBarKeywordAtom, stepSearchBarVisibilityAtom } from '~/atoms/no-code-atoms';
import { useForcedHover } from '~/hooks/useForcedHover';
import { usePreviewFromLocation } from '~/hooks/vm/usePreviewFromLocation';
import type { SelectedStep } from '~/server/logic/prisma/step-select';

export const StepPreviewButton = ({
  step,
  show,
  showScrollToOption,
  disabled,
  testContentId,
}: {
  step: SelectedStep;
  show: boolean;
  showScrollToOption?: boolean;
  disabled: boolean;
  testContentId: string;
}) => {
  const { hovered: baseHovered, ref: optionsBarHoveredRef, setForcedHover } = useForcedHover();
  const shouldShowOptions = show || baseHovered || showScrollToOption;

  const setStepIdToScrollTo = useSetAtom(stepIdToScrollToAtom);
  const setStepSearchBarVisibility = useSetAtom(stepSearchBarVisibilityAtom);
  const searchBarKeyword = useAtomValue(stepSearchBarKeywordAtom.debouncedValueAtom);

  const previewFromLocation = usePreviewFromLocation({ testContentId });

  const handleClickScrollToItem = () => {
    setStepIdToScrollTo(step.id);
    setStepSearchBarVisibility(false);
    setForcedHover(true);
  };

  return (
    <div
      ref={optionsBarHoveredRef}
      className={clsx(!shouldShowOptions && 'hidden')}
    >
      <div className="flex items-center justify-center rounded-md border border-gray-300 dark:border-stone-700">
        {!!searchBarKeyword ?
          <Tooltip
            position="right"
            label="Scroll to this item"
          >
            <ActionIcon
              onClick={handleClickScrollToItem}
              size="md"
              variant="subtle"
              color="gray"
              className="h-6 w-6"
            >
              <IconArrowRight size={16} />
            </ActionIcon>
          </Tooltip>
        : <Tooltip
            position="right"
            label={step.mode === 'AI_DYNAMIC' ? 'Preview to generate' : 'Preview to this step'}
          >
            <ActionIcon
              onClick={() => previewFromLocation({ step })}
              variant="subtle"
              color="green"
              size="md"
              className="h-6 w-6"
              disabled={disabled}
            >
              <IconPlayerPlayFilled size={16} />
            </ActionIcon>
          </Tooltip>
        }
      </div>
    </div>
  );
};
